import { useEffect } from "react";
import ReactGA from "react-ga4";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import Studio from "./pages/Studio";
import ChangePassword from "./pages/ChangePassword";
import AccountSettings from "./pages/AccountSettings";
import ClientDashboard from "./pages/ClientDashboard";
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import Clients from "./pages/admin/Clients";
import ForgotPassword from "./pages/admin/ForgotPassword";
import ResetPassword from "./pages/admin/ResetPassword";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import AffiliateProgram from "./pages/AffiliateProgram";
import AdminRegister from "./pages/admin/Register";
import Guides from "./pages/guides/Index";
import External404Redirect from "./components/External404Redirect";
import "./i18n/config";

const queryClient = new QueryClient();

// Helper function to safely resolve ReactGA across CJS/ESM bundling differences in production
const getGAInstance = () => {
  const mod = ReactGA as any;
  if (typeof mod?.initialize === "function") return mod;
  if (typeof mod?.default?.initialize === "function") return mod.default;
  return null;
};

const TrackPageView = () => {
  const location = useLocation();

  // Initialize GA once on mount
  useEffect(() => {
    try {
      const ga = getGAInstance();
      if (ga) {
        ga.initialize("G-F9G7FG2D7Y");
        console.log("Google Analytics initialized successfully");
      } else {
        console.error("Google Analytics initialize function not found on module");
      }
    } catch (error) {
      console.error("Failed to initialize Google Analytics:", error);
    }
  }, []);

  // Handle subdomain redirects & route page views
  useEffect(() => {
    const currentSubdomain = window.location.hostname; // Get current subdomain
    if (currentSubdomain === "booking.praxipro.io") {
      const newUrl = window.location.href.replace("booking", "fitcalendar");
      window.location.replace(newUrl); // Redirect to the new subdomain
      return;
    }

    // Safely send page view
    try {
      const ga = getGAInstance();
      if (ga && typeof ga.send === "function") {
        ga.send({
          hitType: "pageview",
          page: location.pathname + location.search,
        });
      }
    } catch (error) {
      console.warn("Failed to send pageview to GA:", error);
    }
  }, [location]);

  return null; // Component doesn't render anything
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <TrackPageView />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/studio/:studioName" element={<Studio />} />
            <Route path="/studio/" element={<Studio />} />
            <Route path="/change-password/" element={<ChangePassword />} />
            <Route path="/account-settings/" element={<AccountSettings />} />
            <Route path="/client-dashboard/" element={<ClientDashboard />} />
            <Route path="/admin/login/" element={<Login />} />
            <Route path="/admin/forgot-password/" element={<ForgotPassword />} />
            <Route path="/admin/reset-password/" element={<ResetPassword />} />
            <Route path="/admin/dashboard/" element={<Dashboard />} />
            <Route path="/admin/clients/" element={<Clients />} />
            <Route path="/admin/register/" element={<AdminRegister />} />
            <Route path="/pricing/" element={<Pricing />} />
            <Route path="/contact/" element={<Contact />} />
            <Route path="/affiliate-program/" element={<AffiliateProgram />} />
            <Route path="/terms/" element={<Terms />} />
            <Route path="/privacy/" element={<Privacy />} />
            <Route path="/guides/" element={<Guides />} />
            <Route path="*" element={<External404Redirect />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;